import { DoclistTreeItemSectionsItem } from "~/api/model";

export interface SimilarityGroup {
  identical: DoclistTreeItemSectionsItem[][];
  verySimilar: DoclistTreeItemSectionsItem[][];
}

const extractKeywords = (text: string): string[] => {
  const matches = text.toLowerCase().match(/\b[a-z0-9]{3,}\b/g);
  return matches ? matches.filter(Boolean) : [];
};

export function detectSimilarSectionGroups(
  sections: DoclistTreeItemSectionsItem[]
): SimilarityGroup {
  const identical: DoclistTreeItemSectionsItem[][] = [];
  const verySimilar: DoclistTreeItemSectionsItem[][] = [];
  const processed = new Set<string>();

  sections.forEach((section, index) => {
    if (processed.has(section.id)) return;

    const duplicates = sections.slice(index + 1).filter((other) => {
      if (processed.has(other.id)) return false;

      if (section.name === other.name) {
        const pageDifference = Math.abs(section.pageCount - other.pageCount);
        const maxPages = Math.max(section.pageCount, other.pageCount);
        const percentDifference = maxPages > 0 ? pageDifference / maxPages : 0;
        return percentDifference <= 0.5;
      }

      const sectionLower = section.name.toLowerCase();
      const otherLower = other.name.toLowerCase();
      const hasSubstringMatch =
        sectionLower.includes(otherLower) || otherLower.includes(sectionLower);

      if (hasSubstringMatch) {
        const pageDifference = Math.abs(section.pageCount - other.pageCount);
        const maxPages = Math.max(section.pageCount, other.pageCount);
        const percentDifference = maxPages > 0 ? pageDifference / maxPages : 0;
        return percentDifference < 0.3;
      }

      const sectionKeywords = extractKeywords(section.name);
      const otherKeywords = extractKeywords(other.name);

      if (sectionKeywords.length >= 3 && otherKeywords.length >= 3) {
        const commonKeywords = sectionKeywords.filter((keyword) =>
          otherKeywords.includes(keyword)
        );
        const similarity =
          commonKeywords.length /
          Math.max(sectionKeywords.length, otherKeywords.length);

        if (similarity >= 0.75) {
          const pageDifference = Math.abs(section.pageCount - other.pageCount);
          const maxPages = Math.max(section.pageCount, other.pageCount);
          const percentDifference =
            maxPages > 0 ? pageDifference / maxPages : 0;
          return percentDifference < 0.3;
        }
      }

      return false;
    });

    if (duplicates.length > 0) {
      const group = [section, ...duplicates];

      const allExactNames = group.every((s) => s.name === section.name);
      const allSimilarPageCounts = group.every((s) => {
        const pageDifference = Math.abs(s.pageCount - section.pageCount);
        const maxPages = Math.max(s.pageCount, section.pageCount);
        return maxPages === 0 || pageDifference / maxPages <= 0.2;
      });

      if (allExactNames && allSimilarPageCounts) {
        identical.push(group);
      } else {
        verySimilar.push(group);
      }

      group.forEach((s) => processed.add(s.id));
    }
  });

  return { identical, verySimilar };
}

export function hasAnyDuplicates(
  sections: DoclistTreeItemSectionsItem[]
): boolean {
  const groups = detectSimilarSectionGroups(sections);
  return groups.identical.length > 0 || groups.verySimilar.length > 0;
}
